import React, { FC, PropsWithChildren, useRef, useEffect } from 'react';
import { observer } from 'mobx-react';
import { useLocation } from 'react-router-dom';
import useInfraStores from '@app/hooks/useInfraStores';
import BackeeMode from '@backee/backeeMode';
import useScrollTopOnLocationChange from '@app/hooks/useScrollTopOnLocationChange';
import LoginPage from '@app/login/routes/LoginPage';
import LoggingOutPage from '@app/login/routes/LoggingOutPage';
import { useSingleQueryParam } from '@app/hooks/useSingleQueryParam';
import browserHistory from '@app/utils/browserHistory';
import KnoxersManager from '@app/utils/knoxersManager';
import { isLocalhost, isLocalhostHostname } from '@app/utils/browserUtils';

const AuthenticationProvider: FC<PropsWithChildren> = observer(({ children }) => {
  const { authenticationStore, userStore } = useInfraStores<BackeeMode>();
  const appContentContainerRef = useRef<HTMLDivElement | null>(null);
  useScrollTopOnLocationChange(appContentContainerRef);
  const location = useLocation();
  const [returnUrl] = useSingleQueryParam('returnUrl', /^https?:\/\//);

  const { isLoggedIn, logoutLoading } = authenticationStore;

  useEffect(() => {
    // After successful login, redirect to returnUrl if provided
    if (isLoggedIn && returnUrl) {
      // Save auth to localStorage
      if (userStore?.currentSecretId) {
        KnoxersManager.save(userStore.currentSecretId);
      }

      // On localhost, pass auth data via URL parameter since localStorage can't be shared across ports
      let finalReturnUrl = returnUrl;
      if (isLocalhost()) {
        try {
          const returnUrlObj = new URL(returnUrl);

          if (isLocalhostHostname(returnUrlObj.hostname)) {
            const authData = KnoxersManager.getAuthDataAsUrlParam();
            if (authData) {
              returnUrlObj.searchParams.set('auth', authData);
              finalReturnUrl = returnUrlObj.toString();
            }
          }
        } catch (e) {
          // If returnUrl is not a valid URL, use it as is
          console.error('Failed to parse returnUrl', e);
        }
      }

      // Remove returnUrl from URL and redirect
      const newSearch = new URLSearchParams(location.search);
      newSearch.delete('returnUrl');
      const newPath = location.pathname + (newSearch.toString() ? `?${newSearch.toString()}` : '');
      browserHistory.replace(newPath);
      // Redirect to external URL
      window.location.href = finalReturnUrl;
    }
  }, [isLoggedIn, returnUrl, userStore, location]);

  if (logoutLoading) {
    return <LoggingOutPage />;
  }

  if (!isLoggedIn) {
    return <LoginPage />;
  }

  return <>{children}</>;
});

export default AuthenticationProvider;
